// https://github.com/getsentry/sentry/blob/42eda359bf9b18a02ff5dccea9763d846cda780c/src/sentry/static/sentry/app/utils/theme.tsx
import { get } from "lodash-es";
import { ThemeProvider } from "styled-components";

import colors from "common/config/colors.json";

const spaces = {
    "0": "0",
    "0.25": "2px",
    "0.5": "4px",
    "0.75": "6px",
    "1": "8px",
    "1.5": "12px",
    "2": "16px",
    "3": "20px",
    "4": "30px"
};

const fontSizes = {
    "0": "36px", // font-size: ${({ theme }) => theme.headerFontSizeLarge};
    "1": "27px", // font-size: ${({ theme }) => theme.headerFontSizeMedium};
    "2": "20px", // font-size: ${({ theme }) => theme.headerFontSize};
    "3": "16px", // font-size: ${({ theme }) => theme.fontSizeExtraLarge};
    "4": "14px", // font-size: ${({ theme }) => theme.fontSizeLarge};
    "5": "12px", // font-size: ${({ theme }) => theme.fontSizeMedium};
    "6": "10px", // font-size: ${({ theme }) => theme.fontSizeSmall};
    "7": "8px",
    get h0() { return this["0"]; },
    get h1() { return this["1"]; },
    get h2() { return this["2"]; },
    get h3() { return this["3"]; },
    get h4() { return this["4"]; },
    get h5() { return this["5"]; },
    get h6() { return this["6"]; },
    get h7() { return this["7"]; },
    get h8() { return this["8"]; }
};

const fontStyles = {
    regular: "\"nunito_sans_regular\", sans-serif", // font-family: ${({ theme }) => theme.text.regular};
    medium: "\"nunito_sans_semibold\", sans-serif", // font-family: ${({ theme }) => theme.text.medium};
    bold: "\"nunito_sans_bold\", sans-serif", // font-family: ${({ theme }) => theme.text.bold};
    light: "\"nunito_sans_light\", sans-serif" // font-family: ${({ theme }) => theme.text.light};
};

const analyticsSectionColors = {
    authorizations: get(colors, "blue-light"),
    fees: get(colors, "orange-mid"),
    settlements: get(colors, "aqua"),
    sales: get(colors, "aqua"),
    refunds: get(colors, "blue-mid"),
    refundRate: get(colors, "blue-mid"),
    netSettledCash: get(colors, "blue-mid"),
    averageTicket: get(colors, "orange-mid"),
    chargebacks: get(colors, "purple-mid"),
    cashflow: get(colors, "green-mid"),
    misc: get(colors, "key-lime"),
    psd2_fraud: get(colors, "brick"),
    all_fraud: get(colors, "brick"),
    threedsflex: get(colors, "green-dark"),
    fraudsight: get(colors, "green-dark")
};

const ellipsis = (maxWidth = "100%", display = "inline-block") => `
    display: ${display};
    max-width: ${maxWidth};
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
`;

const ellipsis2 = (maxWidth = "100%") => `
    max-width: ${maxWidth};
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
`;

// From base.scss, to be main theme colors
const baseStyles = {
    // Text colors
    textColorPrimary: `${colors["gray-black"]} !default`, // this is primary text color
    textColorSecondary: `${colors["gray-mid"]} !default`, // secondary text color where emphasize should be more on the primary
    textColorPrimaryKnockout: `${colors["canvas-mid"]} !default`,

    // Background colors
    backgroundColorPrimary: `${colors["canvas-mid"]} !default`, // grayish background on the page
    backgroundColorSecondary: `${colors["canvas-dark"]} !default`, // one tone darker color for elts on the page

    // Branding colors
    colorCorpPrimary: `${colors.red} !default`,
    colorCorpSecondary: `${colors["green-mid"]} !default`,
    
    // Misc colors
    colorActiveLink: `${colors["green-mid"]} !default`,

    // Dimensions
    "page-padding": "16px !default",
    "popover-max-width": "500px !default",
    "sidebar-width": "180px !default",
    "sidebar-padding-large": "180px !default",
    "sidebar-small-width": "50px !default",
    "sidebar-padding-small": "50px !default",
    "sidebar-padding-small-plus-scroll": "70px !default",
    "sidebar-zindex": "1028 !default",

    "dimension-topbar-height": "50px !default",
    "dimension-topbar-border-width": "1px !default",

    "tab-bar-height": "39px !default",
    "top-content-bar-height": "62px !default"
};

export const theme = {
    analyticsSectionColor: (section) => get(analyticsSectionColors, section),
    font: (style, size) => `font-family: ${fontStyles[style]};\nfont-size: ${fontSizes[size]};`, // Backwards compatibility with `@include font(style, size)` rule
    space: (size) => spaces[size],
    size: (size) => fontSizes[size], // Backwards compatibility with current typography system (h0..h7)
    
    fontSizeExtraSmall: "8px",
    fontSizeSmall: "10px",
    fontSizeMedium: "12px",
    fontSizeLarge: "14px",
    fontSizeExtraLarge: "16px",
    headerFontSize: "20px",
    headerFontSizeMedium: "27px",
    headerFontSizeLarge: "36px",
    text: { ...fontStyles },
    mixins: {
        ellipsis,
        ellipsis2
    },

    borderRadius: "2px",

    colors,
    textColor: baseStyles.textColorPrimary,
    backgroundColor: baseStyles.backgroundColorPrimary,
    colorCorp: baseStyles.colorCorpPrimary,
    baseStyles
};

export const withTheme = (Component, selectedTheme = theme) => <ThemeProvider theme={selectedTheme}>{Component}</ThemeProvider>;
